import { CheckIcon, PlusIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import { useId } from 'react';
import type { HomeEntry, HomePlacement } from 'types/homeWidgets';
import { t } from 'utils/getTranslations';
import { addEntry, addsGap } from '../grid/layout';

type Props = {
    /** Already on the Home being arranged. */
    onHome: boolean;
    /** Home holds as many entries as it may: nothing can be added, and the dialog's footer says why. */
    full: boolean;
    /** The layout being arranged and what the button adds to it: added last at its smallest, it may leave a gap. */
    entries: HomeEntry[];
    placement: HomePlacement;
    onAdd: () => void;
};

/** What a card of the Add a widget dialog ends with: the button that adds it, or that it is on Home already. */
export function AddToHome({ onHome, full, entries, placement, onAdd }: Props) {
    const gapId = useId();
    if (onHome) {
        return (
            <span className="text-primary-ink-muted text-meta mt-1 flex h-8 items-center gap-1.5">
                <CheckIcon className="size-3.5" />
                {t('home.widgets.on-home')}
            </span>
        );
    }
    if (full) {
        return null;
    }
    const gap = addsGap(entries, addEntry(entries, placement));
    const button = (
        <Button
            variant="outline"
            size="sm"
            disabled={gap}
            focusableWhenDisabled
            aria-describedby={gap ? gapId : undefined}
            className="border-border-control mt-1 self-start pe-3 aria-disabled:cursor-default aria-disabled:opacity-50"
            onClick={onAdd}
        >
            <PlusIcon className="size-3.5" />
            {t('home.widgets.add-to-home')}
        </Button>
    );
    // Off, it stays in reach, to say why on hover and to a screen reader.
    return gap ? (
        <>
            <Tooltip content={t('home.widgets.leaves-gap')}>{button}</Tooltip>
            <span id={gapId} hidden>
                {t('home.widgets.leaves-gap')}
            </span>
        </>
    ) : (
        button
    );
}
